import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { GALLERY, GALLERY_FILTERS } from "../lib/site";

export const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.type === filter);

  return (
    <section id="gallery" data-testid="gallery-section" className="scroll-mt-20 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
        <Reveal>
          <Overline>Recent work</Overline>
          <div className="mt-6 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <h2 className="max-w-2xl font-display text-4xl font-semibold tracking-tight text-lec-ink sm:text-5xl lg:text-6xl">
              Proof, in concrete.
            </h2>
            <p className="max-w-md text-base text-lec-charcoal">
              Real jobs from across the southern suburbs and beyond — every photo here is our
              own work.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-[background-color,color] duration-200 ${
                  filter === f
                    ? "bg-lec-ink text-lec-green"
                    : "border border-lec-ink/20 text-lec-charcoal hover:border-lec-ink hover:text-lec-ink"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                key={g.src}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className="group relative mb-5 break-inside-avoid overflow-hidden"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.src}
                  alt={`${g.label} — ${g.location}`}
                  loading="lazy"
                  className={`w-full object-cover transition-[transform,filter] duration-700 ease-out group-hover:scale-105 ${
                    i % 3 === 1 ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                />
                <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-lec-ink/90 to-transparent p-5 pt-12 opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-display text-sm font-semibold text-lec-bone">{g.label}</p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-lec-green">
                    {g.type} · {g.location}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
